/**
 * SearchableSelect — generic drop-in replacement for shadcn ``<Select>`` when
 * the option list is long enough that a search box pays for itself.
 *
 * Convention across the project: any picker with **more than 5 options** uses
 * this component instead of a plain ``<Select>``. The search input auto-hides
 * when the underlying option list is ≤ 5, so behaviour degrades to a normal
 * dropdown for short lists.
 *
 * Rendering:
 *   The dropdown panel is portaled to ``document.body`` with fixed
 *   positioning. This is required because most of our dialogs use
 *   ``overflow-y-auto max-h-[90vh]``, which would otherwise clip the panel
 *   when the trigger sits near the bottom of the modal. Position is
 *   recomputed on scroll / resize, and the panel flips above the trigger
 *   when there isn't enough room below.
 *
 *   Self-contained — NOT Radix Popover — so it works inside Sheet/Dialog
 *   without the modal pointer-events trap.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/components/ui/input'

export type SearchableSelectOption = {
  value: string
  label: string
  /** Optional secondary text shown in muted style below the label. */
  hint?: string
  /** Extra searchable text not displayed (e.g. aliases, codes). */
  keywords?: string
}

type Props = {
  value: string | undefined
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  /** When set, never shows the search input even for long lists. */
  noSearch?: boolean
  /** Threshold (exclusive) above which the search input appears. Default 5. */
  searchThreshold?: number
  id?: string
  disabled?: boolean
  /** Custom empty-state text when there are 0 options. */
  emptyLabel?: string
  /** Optional className for the trigger button. */
  className?: string
}

/** Max height of the dropdown panel itself, in pixels. */
const PANEL_MAX_HEIGHT = 320
/** Gap between trigger and panel. */
const GAP = 4

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Pick one…',
  noSearch = false,
  searchThreshold = 5,
  id,
  disabled = false,
  emptyLabel = 'No options available',
  className = '',
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fixed-positioned coords for the portal-rendered panel.
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; placement: 'below' | 'above' } | null>(null)

  // Detect whether we sit inside an OPEN Radix Dialog/Sheet (modal=true).
  // Radix sets pointer-events on body and blocks clicks on portal siblings
  // outside its content tree — so we MUST render the panel inline (inside
  // the dialog's DOM) for clicks to register. Outside a Radix modal we
  // portal to body so we escape any overflow:auto clipping.
  const [insideRadixDialog, setInsideRadixDialog] = useState(false)
  useLayoutEffect(() => {
    if (!open) return
    const t = triggerRef.current
    if (!t) return
    let el: HTMLElement | null = t
    while (el) {
      if (el.getAttribute('role') === 'dialog' && el.getAttribute('data-state') === 'open') {
        setInsideRadixDialog(true)
        return
      }
      el = el.parentElement
    }
    setInsideRadixDialog(false)
  }, [open])

  // Outside-click and Escape close the dropdown. We have to check BOTH the
  // trigger wrapper AND the portal panel — the panel is detached in the DOM.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = triggerRef.current
      const p = panelRef.current
      const target = e.target as Node
      if ((t && t.contains(target)) || (p && p.contains(target))) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Compute / recompute the panel coords whenever it opens, the viewport
  // changes, or any ancestor scrolls (so the panel stays glued to the
  // trigger inside a scrolling modal).
  const computeCoords = () => {
    const t = triggerRef.current
    if (!t) return
    const r = t.getBoundingClientRect()
    const vh = window.innerHeight
    const spaceBelow = vh - r.bottom - GAP
    const spaceAbove = r.top - GAP
    // Prefer below; flip above when below is short and above has more room.
    const wantHeight = Math.min(PANEL_MAX_HEIGHT, spaceBelow)
    const below = spaceBelow >= 160 || spaceBelow >= spaceAbove
    const placement: 'below' | 'above' = below ? 'below' : 'above'
    const top = placement === 'below' ? r.bottom + GAP : Math.max(8, r.top - GAP - Math.min(PANEL_MAX_HEIGHT, spaceAbove))
    setCoords({ top, left: r.left, width: r.width, placement })
    // Hint here for the inner scroll cap — read in render via coords.
    void wantHeight
  }

  useLayoutEffect(() => {
    if (!open) { setCoords(null); return }
    computeCoords()
    // Recompute on scroll (capture so ancestor scrolls fire too) and resize.
    const onScroll = () => computeCoords()
    const onResize = () => computeCoords()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  const showSearch = !noSearch && options.length > searchThreshold

  const filtered = useMemo(() => {
    if (!showSearch) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint || '').toLowerCase().includes(q) ||
        (o.keywords || '').toLowerCase().includes(q),
    )
  }, [options, query, showSearch])

  const selected = value !== undefined ? options.find((o) => o.value === value) : undefined
  const empty = options.length === 0

  // Shared panel body — rendered identically in both the portal path
  // (outside Radix modals) and the inline path (inside Radix modals).
  const panelBody = (
    <>
      {showSearch && (
        <div className="p-2 border-b shrink-0">
          <Input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-8 text-sm"
            autoFocus
          />
        </div>
      )}
      <div className="overflow-y-auto py-1 flex-1 min-h-0">
        {filtered.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground italic">No matches.</div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); setQuery('') }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${
                o.value === value ? 'bg-muted/30' : ''
              }`}
            >
              <div className="text-foreground">{o.label}</div>
              {o.hint && (
                <div className="text-xs text-muted-foreground">{o.hint}</div>
              )}
            </button>
          ))
        )}
      </div>
    </>
  )

  // Derive the available height for the list area so we never overflow the
  // viewport regardless of placement.
  const panelMaxHeight = (() => {
    if (!coords) return PANEL_MAX_HEIGHT
    if (coords.placement === 'below') {
      return Math.max(160, Math.min(PANEL_MAX_HEIGHT, window.innerHeight - coords.top - 8))
    }
    return Math.max(160, Math.min(PANEL_MAX_HEIGHT, (triggerRef.current?.getBoundingClientRect().top || 0) - 8))
  })()

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled || empty}
        onClick={() => setOpen((o) => !o)}
        className={`w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 h-10 text-sm text-left ${
          disabled || empty ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted/30'
        } ${className}`}
      >
        <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground truncate'}>
          {empty ? emptyLabel : selected ? selected.label : placeholder}
        </span>
        <span className="text-muted-foreground text-xs ml-2 shrink-0">▾</span>
      </button>
      {open && !empty && coords && (insideRadixDialog ? (
        <div
          ref={panelRef}
          // Inline render — needed inside Radix Dialog/Sheet because Radix's
          // modal=true blocks pointer events on portal siblings. Absolute
          // positioning relative to this wrapper; flips above the trigger if
          // there isn't enough room below.
          style={{
            position: 'absolute',
            [coords.placement === 'below' ? 'top' : 'bottom']:
              coords.placement === 'below' ? '100%' : '100%',
            left: 0,
            right: 0,
            marginTop: coords.placement === 'below' ? GAP : undefined,
            marginBottom: coords.placement === 'above' ? GAP : undefined,
            zIndex: 50,
            maxHeight: panelMaxHeight,
          }}
          className="rounded-lg bg-popover shadow-md ring-1 ring-foreground/10 flex flex-col"
        >
          {panelBody}
        </div>
      ) : createPortal(
        <div
          ref={panelRef}
          // Portaled to document.body — escapes the modal's overflow:auto so
          // the panel is never clipped by the dialog edge.
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            zIndex: 9999,
            maxHeight: panelMaxHeight,
          }}
          className="rounded-lg bg-popover shadow-md ring-1 ring-foreground/10 flex flex-col"
        >
          {panelBody}
        </div>,
        document.body,
      ))}
    </div>
  )
}
